import { GroupTypes } from "../../../../abstract/controller/enum/GroupTypes";
import * as nodeUtils from "../../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../../abstract/controller/utils/pathUtils";
import { isNonNullish } from "../../../../abstract/controller/utils/systemUtils";

export const showParameterDrop = (srcElement, dstElement, dstElementParent, relVertical) => {
  if (!srcElement) {
    throw new Error("The source node is not defined!");
  }

  if (!dstElement) {
    throw new Error("The destination node is not defined!");
  }

  const _srcId = nodeUtils.getID(srcElement);
  const _dstId = nodeUtils.getID(dstElement);

  let _prevSibling;
  let _child;

  if (relVertical) {
    _prevSibling = nodeUtils.getLastParameter(dstElement);
    _child = nodeUtils.getIdChildWithName(dstElement, nodeUtils.getName(srcElement));
  } else {
    _prevSibling = nodeUtils.getPreviousSiblingParameter(dstElementParent, dstElement);
    _child = nodeUtils.getIdChildWithName(dstElementParent, nodeUtils.getName(srcElement));
  }

  let results = showDropMoveBefore(_srcId, _dstId);

  if (results === false) {
    return false;
  }

  switch (pathUtils.determineGroup(_srcId, _dstId)) {
    case GroupTypes.UNKNOWN:
      return showDropMoveUndefined();
    case GroupTypes.INTERNAL_GLOBALS:
      return showDropMoveInternalGlobals(_srcId, _prevSibling, _child);
    case GroupTypes.INTERNAL_FUNCTIONS:
      return showDropMoveInternalFunctions(_srcId, _prevSibling, _child);
    case GroupTypes.TO_GLOBALS:
      return showDropMoveToGlobals(srcElement, dstElement, _srcId, _dstId, relVertical, _child);
    case GroupTypes.TO_FUNCTIONS:
      return showDropMoveToFunctions(srcElement);
    default:
      throw new Error("Unknown group!");
  }
};

const showDropMoveUndefined = () => {
  return false;
};

const showDropMoveToGlobals = (srcElement, dstElement, srcId, dstId, relVertical, _child) => {
  // We can not move output parameters (with the default value).
  if (isNonNullish(nodeUtils.getValueDefault(srcElement))) {
    return false;
  }

  // We can not move linked parameters (with the target value).
  if (isNonNullish(nodeUtils.getIdTarget(dstElement))) {
    return false;
  }

  // We can not create linked parameters in nested structures.
  if (!Boolean(relVertical) && !nodeUtils.isParameterTopGlobal(dstElement)) {
    return false;
  }

  // We can not move nested output parameters (with the default value).
  if (nodeUtils.hasNestedOutputParameters(srcElement)) {
    return false;
  }

  // We can only create a new top global parameter.
  if (relVertical) {
    // We add the parameter as a child (vertically).
    if (!pathUtils.isFunction(dstId) && !pathUtils.isRoot(dstId)) {
      return false;
    }
  } else {
    // We add the parameter as a sibling (horizontally).
    if (!pathUtils.isParameterTop(dstId)) {
      return false;
    }
  }

  if (!isNameUnique(srcId, _child)) {
    return false;
  }

  return true;
};

const showDropMoveToFunctions = (srcElement) => {
  // We can only move top global parameters.
  if (!nodeUtils.isParameterTopGlobal(srcElement)) {
    return false;
  }

  return true;
};

const showDropMoveInternalGlobals = (srcId, _prevSibling, _child) => {
  return showDropMoveInternal(srcId, _prevSibling, _child);
};

const showDropMoveInternalFunctions = (srcId, _prevSibling, _child) => {
  return showDropMoveInternal(srcId, _prevSibling, _child);
};

const showDropMoveInternal = (srcId, _prevSibling, _child) => {
  if (_prevSibling && _prevSibling?.id === srcId) {
    return false;
  }

  if (!isNameUnique(srcId, _child)) {
    return false;
  }

  return true;
};

const showDropMoveBefore = (srcId, dstId) => {
  // We can not move to the same place.
  if (srcId === dstId) {
    return false;
  }

  // We can not make a parent its own child.
  if (pathUtils.isParent(srcId, dstId)) {
    return false;
  }

  return true;
};

const isNameUnique = (srcId, _child) => {
  if (_child && _child !== srcId) {
    return false;
  }

  return true;
};
